import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';

import { CircleIcon } from '@ui/icons/CircleIcon.tsx';
import { Icon } from '@ui/icons/Icon.tsx';
import { Input2 } from '@ui/inputs/Input2.tsx';
import { SEARCH_RESULTS_CONTAINER_ID } from '@ui/SearchResults/constants.ts';
import Tooltip from '@ui/tooltip';
import { isRtl } from '@utils/dom.ts';

import { useCSSColors } from '../../../../src/hooks/useCSSColors.ts';
import { useIsInitialState } from '../../../../src/hooks/useIsInitialState.tsx';
import { handleKeyboardSelect, isPrintableKey, isSelectKey } from '../../../utils/keyboardUtils.ts';
import {
  InputWrapperStyled,
  SearchBarStyled,
  SearchButton,
  SearchIcon,
  SearchIconWrapper,
  SearchToolTipStyled,
} from './styles.ts';

const NavBar = styled.div`
  display: flex;
  height: 44px;
`;

const NavBarComponent = styled.div`
  flex: 0 1 auto;
`;

const VerticalBar = styled(NavBarComponent)`
  background-color: var(--color-widgetText);
  width: 1px;
  height: 100%;
  margin: 0 6px;
`;

const Input2Styled = styled(Input2)`
  flex: 1 1 auto;
  padding: 0;
`;

const BackButtonToolTipStyled = styled(Tooltip)`
  margin-block: 6px;
  margin-inline: 0px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ReturnHomeFromCustomActionWrapper = styled.button`
  &&& {
    cursor: pointer;
    font-size: 12px;
    display: flex;
    align-items: center;
    margin-bottom: 14px;
    height: 24px;
    background-color: transparent;
    border: none;
    &:focus-visible {
      outline-offset: -2px;
      border-radius: 4px;
    }
  }
`;
const StyledHomeText = styled.u`
  color: var(--color-widgetText);
  margin-inline-start: 4px;
`;
const StyledCustomActionTitle = styled.h1`
  color: var(--color-widgetText);
  font-size: 20px !important;
  margin-inline-start: 12px !important;
  font-weight: 500 !important;
`;

const IconAndTitleWrapper = styled.div`
  display: flex;
  margin-inline-start: -5px;
  align-items: center;
`;

const StyledCircleIcon = styled(CircleIcon)`
  cursor: default;
`;

const SearchNav = ({
  searchPlaceholder,
  onOpenDirectionsSearchButtonClick,
  onSearchTermChange,
  onFocus,
  onBackButtonClicked,
  handleKeyPress,
  widgetState,
  T,
}) => {
  const colors = useCSSColors();
  const [state, setWidgetState] = useState(widgetState.getState());
  const [isSearchActive, setIsSearchActive] = useState(false);
  const inputRef = useRef(null);
  const searchButtonRef = useRef(null);
  const isBackNavigationRef = useRef(false);
  const prevTermRef = useRef(state.term);
  const [isInputFocused, setIsInputFocused] = useState(false);

  useEffect(() => widgetState.addCallback(setWidgetState), [widgetState]);
  const shouldAutoFocus = !useIsInitialState([state.customActionName, state.customActionCategory, isSearchActive]);

  // Focus the search input when the component is displayed and not in custom action mode and not active
  useEffect(() => {
    if (
      !state.customActionName &&
      !state.customActionCategory &&
      !isSearchActive &&
      searchButtonRef.current &&
      shouldAutoFocus
    ) {
      // Defer focus to after paint to ensure button is in DOM
      setTimeout(() => {
        if (searchButtonRef.current && document.activeElement !== inputRef.current) {
          searchButtonRef.current.focus();
        }
      }, 0);
    }
  }, [state.customActionName, state.customActionCategory, isSearchActive, shouldAutoFocus]);

  // app/reset clears the term; that must not be treated as the user clearing the search (which refocuses the input)
  const isInitialResetCount = useRef(true);
  useEffect(() => {
    if (isInitialResetCount.current) {
      isInitialResetCount.current = false;
      return;
    }
    isBackNavigationRef.current = !!prevTermRef.current;
    if (document.activeElement === inputRef.current) {
      inputRef.current.blur();
    }
    setIsInputFocused(false);
    setIsSearchActive(false);
  }, [state.resetCount]);

  useEffect(() => {
    const termCleared = prevTermRef.current && !state.term;
    prevTermRef.current = state.term;

    // If term was cleared by user, ensure focus is kept
    if (termCleared && !isBackNavigationRef.current) {
      focusInput();
      setIsInputFocused(true);
    }

    setIsSearchActive(!!state.term.length);

    if (isBackNavigationRef.current) {
      isBackNavigationRef.current = false;
    }
  }, [state.term]);

  // Set component as focused when input was focused
  const handleFocus = () => {
    onFocus();
    setIsSearchActive(true);
    setIsInputFocused(true);
  };

  const handleBackClick = source => {
    isBackNavigationRef.current = true;
    onBackButtonClicked();
    setIsSearchActive(false);
    if (source !== 'CLEAR') {
      setIsInputFocused(false);
    }
  };

  const handleClearSearch = () => {
    onSearchTermChange('');
    setIsInputFocused(true);
    window.requestAnimationFrame(focusInput);
  };

  const handleInputBlur = e => {
    // Check if focus is moving to the back button
    const backButton = document.querySelector('[data-testid="searchBackButton"]');

    // Only hide the search UI if focus is NOT moving to the back button
    if (e.relatedTarget !== backButton) {
      setIsInputFocused(false);
    }
  };

  const handleCustomActionBackClick = () => {
    isBackNavigationRef.current = true;
    onBackButtonClicked();
  };

  const focusInput = () => inputRef.current && inputRef.current.focus();

  return (
    <div data-testid={'Header'}>
      {state.customActionName && state.customActionCategory ? (
        <>
          <ReturnHomeFromCustomActionWrapper
            onClick={handleCustomActionBackClick}
            data-testid="BackButton"
            tabIndex={0}
            role="button"
            aria-label={T('headerOnline:Home')}
            onKeyDown={handleKeyboardSelect(handleCustomActionBackClick)}
          >
            <Icon
              className="icon"
              width={24}
              height={24}
              name={isRtl() ? 'arrow-right' : 'arrow-left'}
              cdn
              fillColor={colors.widgetIconFill}
            />
            <StyledHomeText>{T('headerOnline:Home')}</StyledHomeText>
          </ReturnHomeFromCustomActionWrapper>
          <IconAndTitleWrapper>
            <StyledCircleIcon
              data-testid="CustomActionIcon"
              aria-hidden="true"
              tabIndex={-1}
              name={state.customActionSVGId ? state.customActionSVGId : state.customActionCategory}
              cdn
              size={36}
            />
            <StyledCustomActionTitle data-testid="CustomActionText" aria-label={`${state.customActionName}`}>
              {state.customActionName}
            </StyledCustomActionTitle>
          </IconAndTitleWrapper>
        </>
      ) : (
        <NavBar>
          {(isSearchActive || isInputFocused) && (
            <BackButtonToolTipStyled text={T('headerOnline:Home')} arrowPosition="top">
              <SearchButton
                data-testid="searchBackButton"
                aria-label={T('headerOnline:Home')}
                onClick={handleBackClick}
                tabIndex={'0'}
                onKeyDown={handleKeyboardSelect(handleBackClick)}
              >
                <Icon
                  name={isRtl() ? 'arrow-right' : 'arrow-left'}
                  cdn
                  width={32}
                  height={32}
                  fillColor={colors.widgetIconFill}
                />
              </SearchButton>
            </BackButtonToolTipStyled>
          )}
          <SearchBarStyled>
            <InputWrapperStyled $isSearchFocused={isInputFocused}>
              {isSearchActive || isInputFocused ? (
                <SearchIconWrapper>
                  <SearchIcon aria-label={T('headerOnline:Search')} data-testid="searchIcon">
                    <Icon name="search" cdn width={32} height={32} fillColor={colors.widgetText} />
                  </SearchIcon>
                </SearchIconWrapper>
              ) : (
                <SearchToolTipStyled text={T('headerOnline:Search')} arrowPosition="top">
                  <SearchButton
                    ref={searchButtonRef}
                    type="button"
                    onClick={focusInput}
                    onKeyDown={e => {
                      if (isSelectKey(e)) {
                        e.preventDefault();
                        focusInput();
                      } else if (isPrintableKey(e)) {
                        focusInput();
                      }
                    }}
                    aria-label={T('headerOnline:Search')}
                    data-testid="searchIconButton"
                  >
                    <Icon name="search" cdn width={32} height={32} fillColor={colors.widgetText} />
                  </SearchButton>
                </SearchToolTipStyled>
              )}

              <Input2Styled
                ref={inputRef}
                color={colors.widgetText}
                placeholder={searchPlaceholder}
                onKeyPress={handleKeyPress}
                value={state.term}
                onChange={onSearchTermChange}
                onFocus={handleFocus}
                title={searchPlaceholder}
                tabIndex={isSearchActive || isInputFocused ? '0' : '-1'}
                handleClearButtonKeyDown={handleKeyboardSelect(handleClearSearch)}
                T={T}
                data-testid="SearchNavInput"
                aria-autocomplete="list"
                aria-haspopup={false}
                aria-controls={isInputFocused || state.term ? SEARCH_RESULTS_CONTAINER_ID : undefined}
                onBlur={handleInputBlur}
              />
            </InputWrapperStyled>
            <VerticalBar />
            <NavBarComponent>
              <Tooltip text={T('headerOnline:Get Directions')} arrowPosition="left">
                <SearchButton
                  onClick={onOpenDirectionsSearchButtonClick}
                  data-testid={'OpenNavigationButton'}
                  aria-label={T('headerOnline:Get Directions')}
                >
                  <Icon name="directions-filled" cdn height={32} width={32} fillColor={colors.widgetIconFill} />
                </SearchButton>
              </Tooltip>
            </NavBarComponent>
          </SearchBarStyled>
        </NavBar>
      )}
    </div>
  );
};
SearchNav.propTypes = {
  searchPlaceholder: PropTypes.string,
  onOpenDirectionsSearchButtonClick: PropTypes.func.isRequired,
  widgetState: PropTypes.object.isRequired,
  onSearchTermChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
};

export default SearchNav;
